import {
  FetchParams,
  FetchResponse,
  ColumnDefinition,
  SortModel,
  KeyValuePair,
} from '../../models';
import { processColumns } from './processColumns';
import { getSubtotalRowFields, markColumnGroupStarts } from './pivotRows';
import { isBuCodeFilter } from '../../functions/buCodeFilter';

export interface LoadDataParams {
  fetchData: (params: FetchParams) => Promise<FetchResponse>;
  pageSize: number;
  sortModel: SortModel[];
  filterValues: KeyValuePair[];
  currentPage: number;
  isLoadingMore: boolean;
}

export type FilterOptionValues = {
  BU_CODE: string[];
  COUNTRY_CODE: string[];
  [key: string]: string[];
};

export interface LoadDataResult {
  data: any[];
  columns: ColumnDefinition[];
  pivotColumns: ColumnDefinition[] | null;
  rowGroupFields: string[];
  filterOptionValues: FilterOptionValues;
  hasMoreData: boolean;
  nextPage: number;
}

export const loadData = async ({
  fetchData,
  pageSize,
  sortModel,
  filterValues,
  currentPage,
  isLoadingMore,
}: LoadDataParams): Promise<LoadDataResult> => {
  const page = isLoadingMore ? currentPage : 1;
  if (!filterValues.some(isBuCodeFilter)) {
    throw new Error('A BU_CODE filter is required to load report data');
  }

  const params: FetchParams = {
    offset: (page - 1) * pageSize,
    limit: pageSize,
    sortModel,
    filters: filterValues,
  };

  const result: FetchResponse = await fetchData(params);

  if (!result.columns) {
    throw new Error('No columns data received');
  }

  const updatedColumns = processColumns(result.columns);
  const newData = result.data || [];

  // If total_count is valid (greater than 0), use it to determine if there's more data
  // Otherwise, assume there's more data if we received a full page of results
  const hasMoreData =
    result.total_count > 0
      ? result.total_count >
        (isLoadingMore
          ? (currentPage - 1) * pageSize + newData.length
          : newData.length)
      : newData.length === pageSize; // If we got a full page, assume there might be more

  return {
    data: newData,
    columns: updatedColumns,
    pivotColumns: result.pivot_columns
      ? markColumnGroupStarts(processColumns(result.pivot_columns))
      : null,
    rowGroupFields: getSubtotalRowFields(result.pivot),
    filterOptionValues: result.filter_values || {},
    hasMoreData,
    nextPage: isLoadingMore ? currentPage + 1 : 2,
  };
};
